import type { FavouritedEvent, RatedEvent, SkippedEvent } from "@trackmark/core";
import { type OutboxEntry, undoEntry } from "./outbox.js";

/**
 * Every way a keystroke in the booth becomes a durable write.
 *
 * This is the boundary the rate loop is built around: the machine decides what happened, the
 * outbox decides when it reaches the server, and this decides what to send. It lives in its own
 * module because it was inline in `Booth.tsx`, where nothing could reach it — and one of the four
 * mappings was wrong for a year. Undo sent `unrate` whether the song had a previous rating or
 * not, so pressing Z after changing an 8 to a 10 deleted the 8 while the card went on showing it.
 */

/** Injected so a test can assert on what was written rather than on when it happened. */
export interface WriteClock {
  now: () => Date;
  eventId: () => string;
}

const systemClock: WriteClock = {
  now: () => new Date(),
  eventId: () => crypto.randomUUID(),
};

/** The half of the outbox this module needs, so a test can pass a list. */
export interface WriteSink {
  enqueue(entry: Omit<OutboxEntry, "attempts">): void;
}

export interface BoothWrites {
  onRated: (event: RatedEvent) => void;
  onUndone: (songId: number, restored: number | null) => void;
  onSkipped: (event: SkippedEvent) => void;
  onFavourited: (event: FavouritedEvent) => void;
}

export function boothWrites(sink: WriteSink, clock: WriteClock = systemClock): BoothWrites {
  return {
    onRated: (event) =>
      sink.enqueue({
        clientEventId: clock.eventId(),
        kind: "rate",
        songId: event.songId,
        value: event.value,
        // The machine's own timestamp, not the clock's: a sitting that syncs an hour later keeps
        // the moment the judgement was actually made.
        ratedAt: event.ratedAt.toISOString(),
      }),

    // `restored` decides whether this is a retraction or a correction back to what the song was.
    onUndone: (songId, restored) =>
      sink.enqueue(undoEntry(songId, restored, clock.eventId(), clock.now().toISOString())),

    onSkipped: (event) =>
      sink.enqueue({
        clientEventId: clock.eventId(),
        kind: "skip",
        songId: event.songId,
        ratedAt: clock.now().toISOString(),
      }),

    onFavourited: (event) =>
      sink.enqueue({
        clientEventId: clock.eventId(),
        kind: "favourite",
        songId: event.songId,
        // The RESOLVED state, never a toggle — the outbox retries, and a retried toggle would
        // flip the song back.
        favourite: event.favourite,
        ratedAt: clock.now().toISOString(),
      }),
  };
}
